import ReactApexChart from 'react-apexcharts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { combinedChartDataOptions, brushChartOptions } from './chartOptions';
import { useCombinedChartState } from './useCombinedChartData';

import HeatMapChartCart from 'components/DevicesDetail/MCT/TrapDetailCharts/HeatMapChart/HeatMapChartCart';
import MCTDetailChartsTitle from 'pages/DevicesDetail/MCT/MCTDetailCharts/MCTDetailChartsTitle';
import { MLButton } from 'components/Button/Button';
import './ChartCard.scss';
import { useAppDispatch, useAppSelector } from 'slices';
import { ChartTypeSRBM } from 'components/DevicesDetail/MCT/ChartType/MCTChartType';
import { setCurrentChartTypeSRBM } from 'pages/DevicesDetail/Slice/devicesDetailSliceSRBM';
import { TrapType } from 'components/AnalyticsPage/TrapType';
import HeatMapYearDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapYearDropdown';
import HeatMapMonthDropdown from 'pages/DevicesDetail/MCT/MCTDetailCharts/Components/HeatMapMonthDropdown';

interface CombinedChartSRBMProps {
  slug: string;
}
interface TabPanelProps {
  children?: React.ReactNode;
  chartName: string;
  currentChart: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, currentChart, chartName } = props;

  return (
    <div hidden={currentChart !== chartName} id={`Table-${chartName}`}>
      {currentChart === chartName && <>{children}</>}
    </div>
  );
}
const CombinedChartSRBM: React.FC<CombinedChartSRBMProps> = ({ slug }) => {
  const { chartData, setIsPercents, isPercents, showLabels, setShowLabels, refetch } = useCombinedChartState({ slug });
  const { t } = useTranslation();
  useEffect(() => {
    refetch();
  }, [slug, refetch]);

  const chartOptions = combinedChartDataOptions(chartData, t, isPercents, showLabels);
  const dispatch = useAppDispatch();
  const currentChart = useAppSelector(state => state.DevicesDetailPagesSRBM.currentSRBMChartType);
  const [currentYear, setCurrentYear] = useState<string>(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [compareMonth, setCompareMonth] = useState<string | null>(null);
  const [isCompare, setIsCompare] = useState<boolean>(false);
  const [chartHeaight, setChartHeight] = useState<number>(500);
  const [compareYear, setCompareYear] = useState<string>(new Date().getFullYear().toString());
  const [isBarChart, setIsBarChart] = useState(true);
  const shouldShowToggle = !(
    currentChart === ChartTypeSRBM.BaitStationChart || currentChart === ChartTypeSRBM.HeatMapChart
  );

  const brushOptions = brushChartOptions(chartData);

  return (
    <div className="card d-flex card-height-100 shadow-lg">
      <MCTDetailChartsTitle
        currentYear={currentYear}
        setCurrentYear={setCurrentYear}
        setSelectedMonth={setSelectedMonth}
        selectedMonth={selectedMonth}
        setChartHeight={setChartHeight}
        isBarChart={isBarChart}
        setIsBarChart={setIsBarChart}
        showSwitchChartType={shouldShowToggle}
        isHeatmapCompare={isCompare}
        setIsHeatmapCompare={setIsCompare}
        setCompareMonth={setCompareMonth}
        setCompareYear={setCompareYear}
        currentChartType={currentChart}
        charts={Object.values(ChartTypeSRBM)}
        onChartTypeChange={value => dispatch(setCurrentChartTypeSRBM(value as ChartTypeSRBM))}
        showChartNameSuffix={false}
      />
      <CustomTabPanel currentChart={currentChart} chartName={ChartTypeSRBM.BaitStationChart}>
        <div className="card-header m-0 p-0">
          <div className="d-flex flex-column flex-lg-row justify-content-between mx-3 my-1">
            <div className="d-flex align-items-center">
              <div className="d-sm-flex align-items-center form-check form-switch form-switch-right form-switch-md mx-3">
                <label className="form-label text-nowrap text-muted">{t('SBSDetail.chart.fiters.showLabels')}</label>
                <input
                  className="form-check-input code-switcher"
                  type="checkbox"
                  id="custom-checkboxes-showcode"
                  onChange={() => setShowLabels(!showLabels)}
                />
              </div>
            </div>
            <div className="d-flex mx-2">
              <MLButton
                color="info"
                innerText={t('SBSDetail.chart.fiters.inGrams')}
                margin={1}
                disabled={!isPercents}
                onClick={() => {
                  setIsPercents(!isPercents);
                }}
              />
              <MLButton
                color="warning"
                innerText={t('SBSDetail.chart.fiters.inPercent')}
                margin={1}
                disabled={isPercents}
                onClick={() => {
                  setIsPercents(!isPercents);
                }}
              />
            </div>
          </div>
        </div>
      </CustomTabPanel>

      <div className="card-body">
        <CustomTabPanel currentChart={currentChart} chartName={ChartTypeSRBM.BaitStationChart}>
          <ReactApexChart options={chartOptions} series={chartOptions.series} type="area" height={350} />
          <ReactApexChart options={brushOptions} series={brushOptions.series} type="line" height={100} />
        </CustomTabPanel>
        <CustomTabPanel currentChart={currentChart} chartName={ChartTypeSRBM.HeatMapChart}>
          <div>
            <HeatMapChartCart
              slug={slug || ''}
              type={TrapType.SRBM}
              currentYear={currentYear}
              selectedMonth={selectedMonth}
              setSelectedMonth={e => {
                setSelectedMonth(e);
                if (isCompare && compareMonth == null) {
                  setCompareMonth(e);
                }
              }}
              chartHeaight={chartHeaight}
              setChartHeight={setChartHeight}
            />
            {isCompare && (
              <div>
                <div className="d-flex flex-row gap-2 alight-items-center justify-content-start px-4">
                  <HeatMapYearDropdown
                    currentYear={compareYear}
                    setCurrentYear={setCompareYear}
                    selectedMonth={compareMonth}
                    setSelectedMonth={setCompareMonth}
                  />
                  {selectedMonth && (
                    <HeatMapMonthDropdown
                      selectedMonth={compareMonth!}
                      setSelectedMonth={e => {
                        setCompareMonth(e);
                        if (isCompare && selectedMonth == null) {
                          setSelectedMonth(e);
                        }
                      }}
                    />
                  )}
                  {selectedMonth && (
                    <ul className="pagination p-0 m-0">
                      <button
                        className="page-link"
                        style={{ cursor: 'pointer', color: '#6A37D4', fontFamily: 'Poppins, sans-serif' }}
                        onClick={() => {
                          setSelectedMonth(null);
                          setCompareMonth(null);
                          setChartHeight(400);
                        }}
                      >
                        back to year
                      </button>
                    </ul>
                  )}
                </div>
                <HeatMapChartCart
                  slug={slug || ''}
                  type={TrapType.SRBM}
                  currentYear={compareYear}
                  selectedMonth={compareMonth}
                  setSelectedMonth={e => {
                    setCompareMonth(e);
                    setSelectedMonth(e);
                  }}
                  chartHeaight={chartHeaight}
                  setChartHeight={setChartHeight}
                  isCompare
                />
              </div>
            )}
          </div>
        </CustomTabPanel>
      </div>
    </div>
  );
};

export default CombinedChartSRBM;
